import {
  Color,
  DataTexture,
  Float32BufferAttribute,
  FloatType,
  InstancedBufferAttribute,
  Object3D,
  RGBAFormat,
} from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils';
import { EdgesGeometryExt } from './EdgesGeometryExt';

function batchGeometries(geometries, edgeThresholdAngle = 10) {
  let colorIdx = [];
  let colorIdxStart = 0;
  let gs = geometries
    .map((g, idx) => {
      // Use configurable threshold angle to control which edges are included
      let ng = new EdgesGeometryExt(g, edgeThresholdAngle);

      // Check if EdgesGeometryExt created valid edges
      if (!ng.attributes.position) {
        return null;
      }

      let instIdx = [];
      for (let i = 0; i < ng.attributes.position.count / 2; i++) {
        instIdx.push(idx);
      }
      ng.setAttribute('instIdx', new Float32BufferAttribute(instIdx, 1));
      let iil = instIdx.length;
      colorIdx.push([colorIdxStart, iil]);
      colorIdxStart += iil;
      return ng;
    })
    .filter((g) => g !== null); // Filter out null geometries

  if (gs.length === 0) {
    return null;
  }

  let fg = BufferGeometryUtils.mergeGeometries(gs);
  fg.userData.colorIdx = colorIdx;
  return fg;
}

class FatLinesBatch extends LineSegments2 {
  constructor(geometries, options = {}) {
    super();
    this.thresholdAngle = { value: 0.785398 };
    this.edgeColor = options.color || '#ffffff';
    this.edgeThickness = options.thickness || 1;
    // Configure edge detection threshold angle (in degrees)
    // Use low threshold (1°) to include most edges — the shader handles
    // silhouette detection per-frame for smooth continuous outlines on curved geometry
    this.edgeThresholdAngle = options.edgeThresholdAngle ?? 1;
    let edges = batchGeometries(geometries, this.edgeThresholdAngle);

    if (!edges) {
      // Initialize with empty items array to prevent errors
      this.items = [];
      this.colorIdx = [];
      // Initialize empty uMediator for update method
      this.uMediator = { value: null };
      // Override update to do nothing when no valid data
      this.update = () => {};
      this.setColorAt = () => {};
      this.dispose = () => {};
      return;
    }

    this.colorIdx = edges.userData.colorIdx;
    let g = new LineSegmentsGeometry();
    g.setPositions(edges.attributes.position.array);
    g.setColors(new Float32Array(edges.attributes.position.array.length).fill(1));
    g.setAttribute('normal0', new InstancedBufferAttribute(edges.attributes.normal0.array, 3));
    g.setAttribute('normal1', new InstancedBufferAttribute(edges.attributes.normal1.array, 3));
    g.setAttribute('instIndex', new InstancedBufferAttribute(edges.attributes.instIdx.array, 1));
    let m = new LineMaterial({
      linewidth: this.edgeThickness,
      worldUnits: false,
      vertexColors: true,
      alphaToCoverage: false,
      transparent: false,
      depthTest: options.depthTest ?? true,
      depthWrite: false,
      onBeforeCompile: (shader) => {
        shader.uniforms.uMediator = this.uMediator;
        shader.uniforms.thresholdAngle = this.thresholdAngle;
        shader.vertexShader = `
            uniform sampler2D uMediator;
            uniform float thresholdAngle;
            attribute float instIndex;
            attribute vec3 normal0;
            attribute vec3 normal1;
            varying float vInstDiscard;
            ${shader.vertexShader}
          `
          .replace(
            `// camera space`,
            `// camera space
            vec4 row0 = texelFetch(uMediator, ivec2(0, int(instIndex)), 0);
            vec4 row1 = texelFetch(uMediator, ivec2(1, int(instIndex)), 0);
            vec4 row2 = texelFetch(uMediator, ivec2(2, int(instIndex)), 0);
            vec4 row3 = texelFetch(uMediator, ivec2(3, int(instIndex)), 0);
            mat4 instMatrix = mat4(row0, row1, row2, row3);
            `
          )
          .replaceAll(`= modelViewMatrix * vec4( instance`, `= modelViewMatrix * instMatrix * vec4( instance`)
          .replace(
            `#include <fog_vertex>`,
            `#include <fog_vertex>

              vec3 mp = (instanceStart + instanceEnd) / 2.;
              vec3 cp0 = mp + cross(normal0, normalize(instanceEnd - instanceStart));
              vec3 cp1 = mp + cross(normal1, normalize(instanceEnd - instanceStart));

              vec4 midEdge = projectionMatrix * modelViewMatrix * instMatrix * vec4(mp, 1.);
              vec4 c0edge = projectionMatrix * modelViewMatrix * instMatrix * vec4(cp0, 1.);
              vec4 c1edge = projectionMatrix * modelViewMatrix * instMatrix * vec4(cp1, 1.);

              vec2 me = midEdge.xy / midEdge.w;
              vec2 c0 = c0edge.xy / c0edge.w;
              vec2 c1 = c1edge.xy / c1edge.w;

              c0 = normalize(c0 - me);
              c1 = normalize(c1 - me);

              vec2 mdir = normalize(vec2(-dir.y, dir.x));

              float d0 = dot(mdir, c0);
              float d1 = dot(mdir, c1);

              bool type5  = sign( d0 ) != sign( d1 );
              bool threshold = dot(normal0, normal1) <= cos(thresholdAngle);

              // Show edge if: it's a silhouette edge OR a sharp crease
              vInstDiscard = float(type5 || threshold);

              // Push lines slightly toward camera to prevent z-fighting with mesh surfaces
              gl_Position.z -= 0.001 * gl_Position.w;
              `
          );
        shader.fragmentShader = `
              varying float vInstDiscard;
              ${shader.fragmentShader}
          `.replace(
          `#include <premultiplied_alpha_fragment>`,
          `#include <premultiplied_alpha_fragment>
              float instDiscard = vInstDiscard;
              if (instDiscard < 0.5) discard;

            `
        );
      },
    });
    this.items = new Array(geometries.length).fill().map((_) => {
      return new Object3D();
    });

    this.geometry = g;
    this.material = m;
    if (options.renderOrder != null) this.renderOrder = options.renderOrder;

    let mediatorWidth = 4;
    let mediatorHeight = geometries.length;
    let mediator = new DataTexture(
      new Float32Array(mediatorWidth * mediatorHeight * 4),
      mediatorWidth,
      mediatorHeight,
      RGBAFormat,
      FloatType
    );
    this.uMediator = { value: mediator };

    this.update = () => {
      this.items.forEach((o, idx) => {
        o.updateMatrix();
        this.uMediator.value.image.data.set(o.matrix.elements, idx * 16);
      });
      this.uMediator.value.needsUpdate = true;
    };

    let _c = new Color();
    this.setColorAt = (idx, color) => {
      // Check if we have valid color indices
      if (!this.colorIdx || idx >= this.colorIdx.length || !this.colorIdx[idx]) {
        return;
      }

      _c.set(color);
      let cStart = this.geometry.attributes['instanceColorStart'];
      let cEnd = this.geometry.attributes['instanceColorEnd'];
      let cIdx = this.colorIdx[idx];
      let cIdxStart = cIdx[0];
      let cIdxLength = cIdx[1];
      for (let i = cIdxStart; i < cIdxStart + cIdxLength; i++) {
        cStart.setXYZ(i, _c.r, _c.g, _c.b);
        cEnd.setXYZ(i, _c.r, _c.g, _c.b);
      }
      cStart.needsUpdate = true;
      cEnd.needsUpdate = true;
    };

    // State tracking for on/off functionality
    this.isEnabled = true;
    this.originalUpdate = this.update;

    // Override update to respect enabled state
    this.update = () => {
      if (this.isEnabled) {
        this.originalUpdate();
      }
    };

    // Turn the FatLinesBatch on
    this.turnOn = () => {
      this.isEnabled = true;
      this.visible = true;
      this.material.visible = true;
      this.material.needsUpdate = true;
      this.originalUpdate();
    };

    // Turn the FatLinesBatch off
    this.turnOff = () => {
      this.isEnabled = false;
      this.visible = false;
      this.material.visible = false;
      this.material.needsUpdate = true;
      this.update();
    };

    // Toggle the FatLinesBatch on/off
    this.toggle = () => {
      if (this.isEnabled) {
        this.turnOff();
      } else {
        this.turnOn();
      }
    };

    // Check if the FatLinesBatch is currently enabled
    this.isOn = () => {
      return this.isEnabled;
    };

    // Update edge color
    this.setEdgeColor = (color) => {
      this.edgeColor = color;
      if (this.colorIdx && this.geometry) {
        // Update all edges with the new color
        for (let idx = 0; idx < this.colorIdx.length; idx++) {
          this.setColorAt(idx, color);
        }
      }
    };

    // Update edge thickness
    this.setEdgeThickness = (thickness) => {
      this.edgeThickness = thickness;
      if (this.material) {
        this.material.linewidth = thickness;
        this.material.needsUpdate = true;
      }
    };

    // Update edge threshold angle (requires rebuilding geometry)
    this.setEdgeThresholdAngle = (angle) => {
      this.edgeThresholdAngle = angle;
    };

    this.dispose = () => {
      if (this.geometry) this.geometry.dispose();
      if (this.material) this.material.dispose();
      if (this.uMediator?.value) this.uMediator.value.dispose();
    };
  }
}
export { FatLinesBatch };
